import { computed } from "vue";
import { usePpWagerConfiguration } from "./use-wager-configuration";
import { storeToRefs } from "pinia";
import { useWagerBoardStore } from "../stores/use-wager-board-store";
import { StringUtils } from "@sazka/utils/string-utils";
import { usePpWagerDurationStore } from "../stores/use-wager-duration-store";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { MathUtils } from "@sazka/utils/math-utils";
import { KasickaStakeOptions } from "../model/kasicka-stake-options";

/**
 * Lottery price calculator, uses store to get data about active lottery
 * @returns total price as number and formatted
 */
export const usePpWagerPriceCalculator = () => {
    const config = usePpWagerConfiguration().config;
    const store = useWagerBoardStore();
    const { getDurationLength } = storeToRefs(usePpWagerDurationStore());

    const sportkaDrawPrice = (): number => {
        let price = store.boardCount * config.basePrice;
        if (store.isSystem) {
            price = config.basePrice * MathUtils.combinations(store.systemPrimaryCount, config.primaryBoardSize);
        }
        price += store.isAddonPlayed ? config.addonPrice : 0;
        return price;
    };

    const eurojackpotDrawPrice = (): number => {
        let price = store.boardCount * config.basePrice;
        if (store.isSystem) {
            price = config.basePrice * MathUtils.combinations(store.systemPrimaryCount, config.primaryBoardSize);
            price = price * MathUtils.combinations(store.systemSecondaryCount, config.secondaryBoardSize);
        }
        price += store.isAddonPlayed ? config.addonPrice : 0;
        return price;
    };

    const kenoDrawPrice = (): number => {
        return store.boards.reduce((acc, board) => acc + board.stake, 0);
    };

    const rychla6DrawPrice = (): number => {
        return store.getBoardsForCheckout.reduce((acc, board) => acc + board.stake, 0);
    };

    const stastnych10DrawPrice = (): number => {
        const price = store.boards.reduce((total, board) => total + board.stake * (board.kralovskaHra ? 2 : 1), 0);
        return price + (store.isAddonPlayed ? config.addonPrice : 0);
    };

    const kasickaStakePrice = (totalNumbers: number, stakeIndex: KasickaStakeOptions): number => {
        return MathUtils.combinations(totalNumbers, stakeIndex + 1) * store.wagerStakes[stakeIndex];
    };

    const kasickaSystemPrice = (): number =>
        store.wagerStakes.reduce((sum, _, index) => {
            // cant bet on combination that is bigger than selected number count (pětice with 4 numbers)
            if (index + 1 > store.systemBoard.primary.length) return sum;
            return sum + kasickaStakePrice(store.systemBoard.primary.length, index);
        }, 0);

    const kasickaDrawPrice = (): number => {
        // non system (klasická sázenka) has a static price of 20kc
        if (!store.isSystem) return store.boardCount * config.basePrice * store.multiplier;
        // system price is calculated sum based on selected number count and selected stake options
        const totalPrice = kasickaSystemPrice();
        return totalPrice * store.multiplier;
    };

    const euromilionyDrawPrice = (): number => {
        let price = store.boardCount * config.basePrice;
        if (store.isSystem) {
            price = config.basePrice * MathUtils.combinations(store.systemPrimaryCount, config.primaryBoardSize);
            price = price * MathUtils.combinations(store.systemSecondaryCount, config.secondaryBoardSize);
        }
        price += store.isAddonPlayed ? config.addonPrice : 0;
        return price;
    };

    const extraRentaDrawPrice = (): number => {
        return store.boardCount * store.extraRentaStake;
    };

    const kamenyDrawPrice = (): number => {
        return store.boards.reduce((acc, board) => acc + board.stake * board.primary.length, 0);
    };

    const stastneDatumDrawPrice = (): number => {
        return store.boardCount * config.basePrice * store.multiplier;
    };

    const drawPrice = computed(() => {
        switch (config.lottery) {
            case Lottery.sportka:
                return sportkaDrawPrice();
            case Lottery.eurojackpot:
                return eurojackpotDrawPrice();
            case Lottery.keno:
                return kenoDrawPrice();
            case Lottery.stastnych10:
                return stastnych10DrawPrice();
            case Lottery.kasicka:
                return kasickaDrawPrice();
            case Lottery.euromiliony:
                return euromilionyDrawPrice();
            case Lottery.extraRenta:
                return extraRentaDrawPrice();
            case Lottery.kameny:
                return kamenyDrawPrice();
            case Lottery.rychla6:
                return rychla6DrawPrice();
            case Lottery.stastneDatum:
                return stastneDatumDrawPrice();
            case Lottery.miniRenta:
            default:
                return store.boardCount * config.basePrice;
        }
    });
    const totalPrice = computed(() => drawPrice.value * getDurationLength.value);
    const totalPriceFormatted = computed(() => StringUtils.formatCurrency(totalPrice.value, true));

    return {
        drawPrice,
        totalPrice,
        totalPriceFormatted,
        kasickaSystemPrice,
    };
};
